import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function Blog() {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
  }, []);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return "";
    }
  };

  const featured = posts && posts.length > 0 ? posts[0] : null;
  const rest = posts && posts.length > 1 ? posts.slice(1) : [];

  return (
    <div className="min-h-screen bg-giva-bg">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 lg:px-8 lg:pt-40">
        <header className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-giva-text2">{t("blogPage.overline")}</span>
          <h1 className="mt-4 font-display text-5xl font-black tracking-tight text-giva-navy sm:text-6xl">{t("blogPage.title")}</h1>
          <p className="mt-5 text-lg text-giva-text2">{t("blogPage.subtitle")}</p>
        </header>

        {posts === null && (
          <div className="flex justify-center py-24" data-testid="blog-loading">
            <Loader2 className="h-8 w-8 animate-spin text-giva-primary" />
          </div>
        )}

        {posts && posts.length === 0 && (
          <p className="py-24 text-center text-giva-text2" data-testid="blog-empty">{t("blogPage.empty")}</p>
        )}

        {featured && (
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group mt-14 grid overflow-hidden rounded-[2rem] border border-giva-line bg-white lg:grid-cols-2"
          >
            {featured.cover_image && <img src={featured.cover_image} alt={featured.title} className="h-72 w-full object-cover lg:h-full" />}
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <span className="text-xs font-semibold uppercase tracking-wider text-giva-accent">{featured.category}</span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-giva-navy group-hover:text-giva-primary lg:text-4xl">{featured.title}</h2>
              <p className="mt-4 text-base leading-relaxed text-giva-text2">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-3 text-sm text-giva-text2">
                <span>{featured.author}</span>
                <span className="h-1 w-1 rounded-full bg-giva-line" />
                <span>{formatDate(featured.created_at)}</span>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-giva-primary">
                {t("blogPage.read")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-giva-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              >
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-48 w-full object-cover" />}
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-giva-accent">{p.category}</span>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug text-giva-navy group-hover:text-giva-primary">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm text-giva-text2">{p.excerpt}</p>
                  <span className="mt-4 text-xs text-giva-text2">{formatDate(p.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
